import React from 'react';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TagTree from '../components/TagTree';
import TTEditor from '../components/TTEditor';
import SearchBar from '../components/SearchBar';
import AIResult from '../components/AIResult';
import SimpleNoteList from '../components/SimpleNoteList';
import { Note, Tag, notesApi } from '../services/notes';
import { tagsApi } from '../services/tags';
import Drawer from '../components/Drawer';
import MenuToggle from '../components/MenuToggle';
import Settings from '../components/Settings';
import { useTranslation } from 'react-i18next';
import { Search, Plus } from 'lucide-react';
import logo from '../assets/logo.svg';  // 添加 logo 导入
import { logEvent } from '../utils/analytics';
import { API_BASE_URL } from '../config';

import '../styles/notes.css';


export default function Notes() {
  const { user, logout, token, userId } = useAuth();
  const navigate = useNavigate();
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tags, setTags] = useState<any[]>([]);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [aiQuestion, setAiQuestion] = useState<string | null>(null);
  const [isCommandMode, setIsCommandMode] = useState(false);
  const [isTagTreeOpen, setIsTagTreeOpen] = useState(true);
  const [currentView, setCurrentView] = useState<'notes' | 'settings'>('notes');
  const [showSearch, setShowSearch] = useState(false);
  const [showMobileEditor, setShowMobileEditor] = useState(false);
  const { t } = useTranslation(['common']);
  const editorRef = useRef<HTMLDivElement>(null);
  const isMobile = window.innerWidth <= 768;
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isWebCapturing, setIsWebCapturing] = useState(false);
  const [webCaptureError, setWebCaptureError] = useState<string | null>(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    // Check authentication status
    if (!user) {
      navigate('/login', { 
        replace: true,
        state: { from: '/notes' } 
      });
      return;
    }

    // Load data if authenticated
    loadTags();
    loadNotes();
  }, [user, navigate]);

  useEffect(() => {
    console.log('selectedTag changed to:', selectedTag);
    loadNotes();
  }, [selectedTag]);

  useEffect(() => {
    // 检查是否为移动设备
    const isMobile = window.innerWidth <= 768;
    // 设置初始状态：桌面端显示，移动端隐藏
    setIsTagTreeOpen(!isMobile);

    // 添加窗口大小变化监听
    const handleResize = () => {
      const isMobile = window.innerWidth <= 768;
      setIsTagTreeOpen(!isMobile);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []); // 仅在组件挂载时执行

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editorRef.current && !editorRef.current.contains(event.target as Node)) {
        setShowMobileEditor(false);
      }
    };

    if (showMobileEditor) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMobileEditor]);

  useEffect(() => {
    // 创建 SSE 连接
    const eventSource = new EventSource('http://localhost:3000/api/events');

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'refresh') {
          // 根据刷新类型执行相应的操作
          if (['notes', 'notes_and_tags'].includes(data.data.type)) {
            // 刷新笔记列表
            loadNotes();
          }
          if (['tags', 'notes_and_tags'].includes(data.data.type)) {
            // 刷新标签列表
            loadTags();
          }
        }
      } catch (error) {
        console.error('Error parsing SSE message:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      eventSource.close();
    };

    // 组件卸载时关闭连接
    return () => {
      eventSource.close();
    };
  }, []); // 空依赖数组，只在组件挂载时执行一次

  const loadNotes = async (isLoadMore = false) => {
    if (isLoadMore && (!hasMore || isLoadingMore)) return;

    if (isLoadMore) {
      setIsLoadingMore(true);
    } else {
      setLoading(true);
      setPage(1); // 重置页码
    }

    setError(null);
    
    try {
      console.log('Loading notes for tag:', selectedTag, 'page:', isLoadMore ? page + 1 : 1);
      const response = await notesApi.fetchNotes(selectedTag, isLoadMore ? page + 1 : 1);
      
      // 检查返回的数据结构
      // console.log('API Response:', response);
      
      // 确保我们正确访问 notes 数组
      const notesData = response.data.notes || response.data;
      
      if (isLoadMore) {
        setNotes(prev => [...prev, ...notesData]);
        setPage(prev => prev + 1);
      } else {
        setNotes(notesData);
      }
      
      // 如果 API 返回了 total 和 hasMore，则使用它们
      if ('total' in response.data) {
        setTotal(response.data.total);
      }
      if ('hasMore' in response.data) {
        setHasMore(response.data.hasMore);
      }
    } catch (err: any) {
      console.error('Error loading notes:', err);
      setError(err.response?.status === 404 
        ? 'No related notes found' 
        : 'Failed to load notes'
      );
    } finally {
      if (isLoadMore) {
        setIsLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleLoadMore = () => {
    if (hasMore && !isLoadingMore) {
      loadNotes(true);
    }
  };

  const handleTagSelect = async (tag: string | null) => {
    console.log('Tag selected:', tag);
    setSelectedTag(tag);
    setInputValue('');
    setIsCommandMode(false);
    setAiQuestion(null);
    setPage(1);
    setHasMore(true);
  };

  const handleNoteCreate = async (content: string) => {
    try {
      const tags: Tag[] = [];
      const lines = content.split('\n');
      
      if (lines[0].startsWith('#')) {
        const tagMatches = lines[0].match(/#[\u4e00-\u9fa5\w]+(?:\/[\u4e00-\u9fa5\w]+)*/g);
        if (tagMatches) {
          tagMatches.forEach(tag => {
            const parts = tag.substring(1).split('/');
            tags.push({
              level1: parts[0],
              level2: parts[1] || null,
              level3: parts[2] || null
            });
          });
          content = lines.slice(1).join('\n').trim();
        }
      }

      const response = await notesApi.createNote({ text: content, tags });
      setNotes(prev => [response.data, ...prev]);
      await loadTags();
      return true;
    } catch (err) {
      console.error('Failed to create note:', err);
      return false;
    }
  };

  const loadTags = async () => {
    try {
      const response = await tagsApi.fetchTags();
      setTags(response.data);
    } catch (error) {
      console.error('Failed to load tags:', error);
    }
  };

  const handleNoteDelete = async (noteId: string) => {
    try {
      await notesApi.deleteNote(noteId);
      setNotes(prev => prev.filter(note => note._id !== noteId));
      await loadTags();
      return true;
    } catch (err) {
      console.error('Failed to delete note:', err);
      return false;
    }
  };

  const handleEdit = (note: Note) => {
    // console.log('Editing note:', note);
    setEditingNote(note);
  };

  const handleInlineEdit = async (noteId: string, content: string, updates?: Partial<Note>) => {
    try {
      // console.log('Updating note:', noteId, content);
      const tags: Tag[] = [];
      const lines = content.split('\n');
      
      if (lines[0].startsWith('#')) {
        const tagMatches = lines[0].match(/#[\u4e00-\u9fa5\w]+(?:\/[\u4e00-\u9fa5\w]+)*/g);
        if (tagMatches) {
          tagMatches.forEach(tag => {
            const parts = tag.substring(1).split('/');
            tags.push({
              level1: parts[0],
              level2: parts[1] || null,
              level3: parts[2] || null
            });
          });
          content = lines.slice(1).join('\n').trim();
        }
      }

      const response = await notesApi.updateNote(noteId, {
        text: content,
        tags: tags,
        updatedAt: new Date().toISOString()
      });
      
      setNotes(prev => prev.map(note => 
        note._id === noteId ? response.data : note
      ));

      await loadTags();
      return true;
    } catch (error) {
      console.error('Failed to update note:', error);
      return false;
    }
  };

  const handleWebCapture = async (url: string) => {
    setIsWebCapturing(true);
    setWebCaptureError(null);
    
    try {
      const response = await fetch('https://memowise.co/wechat/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'user-id': userId || ''
        },
        body: JSON.stringify({
          url: url,
          noteServerUrl: API_BASE_URL
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }      
      // // 等待一段时间确保笔记已创建完成
      await new Promise(resolve => setTimeout(resolve, 5000));
      
      // 刷新笔记和标签
      await Promise.all([
        loadNotes(),
        loadTags()
      ]);

      // 重置状态
      setInputValue('');
      setIsCommandMode(false);
    } catch (error) {
      console.error('Web capture failed:', error);
      setWebCaptureError(error instanceof Error ? error.message : t('common.message.webCaptureError', { error: error }));
    } finally {
      setIsWebCapturing(false);
    }
  };

  const handleSearch = useCallback(async (value: string) => {
    if (value.startsWith('/')) {
      setIsCommandMode(true);
      if (value.startsWith('/ask ')) {
        const question = value.substring(5).trim();
        if (question) {
          setAiQuestion(question);
          logEvent('Command', 'Ask', question);
        }
      } else if (value.startsWith('/parse ')) {
        // 提取URL的正则表达式
        const urlRegex = /(https?:\/\/[^\s]+)/;
        const match = value.substring(7).match(urlRegex);
        
        if (match && match[0]) {
          const url = match[0];
          console.log('提取到URL:', url);
          handleWebCapture(url);  // 不使用 await
          logEvent('Command', 'Parse', url);
        } else {
          console.error('无法从输入中提取有效URL:', value.substring(7));
          // 可以添加一个提示
          setWebCaptureError('无法识别有效的URL，请确保输入了正确的网址');
        }
      }
    } else {
      setIsCommandMode(false);
      setAiQuestion(null);
      if (!value.trim()) {
        loadNotes();
        return;
      }
      try {
        const response = await notesApi.searchNotes(value);
        setNotes(response.data);
      } catch (error) {
        console.error('Search failed:', error);
      }
    }
  }, [loadNotes]);

  const toggleTagTree = () => {
    setIsTagTreeOpen(!isTagTreeOpen);
  };

  const handleUsernameClick = () => {
    setCurrentView('settings');
    if (window.innerWidth <= 768) {
      setIsTagTreeOpen(false);
    }
  };

  const handleBackToNotes = () => {
    setCurrentView('notes');
  };

  return (
    <div className="page-container">
      <div className="notes-container">
        <div className={`notes-layout ${!isTagTreeOpen ? 'collapsed' : ''}`}>
          <Drawer isOpen={isTagTreeOpen} onClose={() => setIsTagTreeOpen(false)}>
            <aside className="tags-panel">
              <div 
                className="user-email"
                onClick={handleUsernameClick}
                style={{ cursor: 'pointer' }}
              >
                {user?.nickname || user?.email}
              </div>
              <TagTree 
                onSelectTag={async (tag) => {
                  try {
                    await handleTagSelect(tag);
                    if (isMobile) {
                      setIsTagTreeOpen(false);
                    }
                  } catch (error) {
                    console.error('Failed to select tag:', error);
                  }
                }}
                tags={tags}
                onTagsUpdate={loadTags}
              />
            </aside>
          </Drawer>

          <main className="notes-panel">
            <header className="main-header">
              <div className="header-content">
                <div className="header-left">
                  <MenuToggle 
                    isOpen={isTagTreeOpen} 
                    onClick={toggleTagTree}
                  />
                  <img 
                    src={logo} 
                    alt="Memowise" 
                    className="header-logo"
                  />
                  <div className="mobile-actions">
                    <button 
                      className="mobile-action-btn" 
                      onClick={() => {
                        setShowSearch(prev => !prev);  // 切换搜索框显示状态
                        setIsCommandMode(false);  // 关闭命令模式
                      }}
                    >
                      <Search size={20} />
                    </button>
                    <button 
                      className="mobile-action-btn" 
                      onClick={() => {
                        setShowMobileEditor(prev => !prev);  // 切换底部编辑器显示状态
                        setShowSearch(false);  // 关闭搜索框
                        setIsCommandMode(false);
                      }}
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                </div>
                <div className="user-info">
                  {/* <span 
                    className="username" 
                    onClick={handleUsernameClick}
                    style={{ cursor: 'pointer' }}
                  >
                    {user?.email}
                  </span> */}
                  <button onClick={handleLogout} className="logout-btn">
                    {t('common.header.logout')}
                  </button>
                </div>
              </div>
            </header>

            <div className="notes-content">
              {currentView === 'settings' ? (
                <Settings onBack={handleBackToNotes} />
              ) : (
                <>
                  {(showSearch || !isMobile) && (
                    <SearchBar
                      value={inputValue}
                      onChange={(value) => {
                        setInputValue(value);
                        handleSearch(value);
                      }}
                    />
                  )}
                  {(!showSearch && !isCommandMode && !isMobile) && (
                    <TTEditor 
                      selectedTag={selectedTag}
                      onSave={handleNoteCreate}
                      className="notes-page-editor"
                    />
                  )}
                  {isCommandMode ? (
                    <>
                      {isWebCapturing && (
                        <div className="loading-message">
                          {t('common.message.webCapturing')}
                        </div>
                      )}
                      {webCaptureError && (
                        <div className="error-message">
                          {t('common.message.webCaptureError', { error: webCaptureError })}
                        </div>
                      )}
                      {aiQuestion && (
                        <AIResult 
                          question={aiQuestion}
                          onSaveNote={handleNoteCreate}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {error ? (
                        <div className="error-message">{error}</div>
                      ) : (
                        <SimpleNoteList 
                          notes={notes}
                          loading={loading}
                          onDelete={handleNoteDelete}
                          onEdit={handleEdit}
                          onInlineEdit={handleInlineEdit}
                          hasMore={hasMore}
                          isLoadingMore={isLoadingMore}
                          onLoadMore={handleLoadMore}
                          total={total}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </main>
        </div>
      </div>
      {showMobileEditor && isMobile && (
        <div className="mobile-editor-overlay">
          <div className="mobile-editor-container" ref={editorRef}>
            <TTEditor 
              selectedTag={selectedTag}
              onSave={async (content) => {
                const success = await handleNoteCreate(content);
                if (success) {
                  setShowMobileEditor(false);  // 保存成功后关闭编辑器
                }
                return success;
              }}
              inline={true}
            />
          </div>
        </div>
      )}
    </div>
  );
} 
